import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Checkbox, FormControlLabel, LinearProgress, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CRM_POLL, CrmIntakeView, crmApi } from '../../../features/crm/crmApi'
import {
    Card, CardHead, CrmDialog, FieldLabel, Hint, Kpis, Muted, OutlineButton, PageHead, Pill, PrimaryButton, TONES, TableWrap, Tone, contentSx, pageSx,
} from './crmUi'
import LegacyDialog from './LegacyDialog'
import { addMonthKey, daysTo, digits, fmtDate, formatPhone, money, todayKey } from './format'
import { CrmDict, useCrmDict } from './dict'
import { payStateView } from './stages'
import { EmptyRow } from '../head/HeadCrmNewPage'

type Mode = 'SELLER' | 'MOD' | 'ROP'

const okBtn = { background: ui.pink, '&:hover': { background: ui.pinkDark } }

// Ішкі рассрочка / айлық: продажник - «Төлем күтетіндер», модератор - «Төлемдер», РОП - «Айлық төлемдер»
const InstallmentsPage: React.FC<{ mode: Mode }> = ({ mode }) => {
    const { formatMessage } = useIntl()
    const dict = useCrmDict()
    const { data = [], isFetching } = crmApi.useListInstallmentsQuery(mode, { pollingInterval: CRM_POLL })
    const [legacy, setLegacy] = useState(false)
    const live = data.filter((x) => x.status !== 'RETURNED' && x.status !== 'DELETED')

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={formatMessage({ id: `crmInstTitle_${mode}` })}
                    sub={formatMessage({ id: `crmInstSub_${mode}` })}
                    action={mode !== 'SELLER' && <PrimaryButton onClick={() => setLegacy(true)}>＋ {formatMessage({ id: 'crmLegBtn' })}</PrimaryButton>}
                />
                {legacy && <LegacyDialog rop={mode === 'ROP'} onClose={() => setLegacy(false)} />}
                {isFetching && !data.length && <LinearProgress sx={{ marginBottom: 1 }} />}
                {mode === 'SELLER' && <SellerView rows={live} dict={dict} />}
                {mode === 'MOD' && <ModView rows={live} dict={dict} />}
                {mode === 'ROP' && <RopView rows={live} dict={dict} />}
            </Box>
        </Box>
    )
}

const LEGEND: [Tone, string][] = [
    ['ok', 'crmLegendOk'],
    ['wait', 'crmLegendSoon'],
    ['bad', 'crmLegendLate'],
    ['blue', 'crmPayPending'],
    ['grey', 'crmLegendNoDate'],
]

const Legend = () => {
    const { formatMessage } = useIntl()
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {LEGEND.map(([tone, id]) => (
                <Pill key={id} tone={tone}>
                    ● {formatMessage({ id })}
                </Pill>
            ))}
        </Box>
    )
}

const State: React.FC<{ x: CrmIntakeView }> = ({ x }) => {
    const { formatMessage } = useIntl()
    const st = payStateView(formatMessage, x)
    return <Pill tone={st.tone}>● {st.text}</Pill>
}

const rowBg = (fm: any, x: CrmIntakeView) => {
    const bg = payStateView(fm, x).row
    return bg ? { background: bg } : undefined
}

const months = (x: CrmIntakeView) => x.paidMonths || 1

// бывший ученик, добавленный вручную
const LegTag: React.FC<{ x: CrmIntakeView }> = ({ x }) => {
    const { formatMessage } = useIntl()
    return x.legacy ? (
        <Pill tone="grey" sx={{ marginLeft: '6px' }}>
            {formatMessage({ id: 'crmLegTag' })}
        </Pill>
    ) : null
}
const nextSum = (x: CrmIntakeView) => x.nextPaySum ?? x.data?.nextPaySum ?? 0

const SellerView: React.FC<{ rows: CrmIntakeView[]; dict: CrmDict }> = ({ rows, dict }) => {
    const { formatMessage } = useIntl()
    const [paying, setPaying] = useState<CrmIntakeView | null>(null)
    return (
        <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Legend />
            <TableWrap minWidth={720}>
                <table>
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: 'crmColStudent' })}</th>
                            <th>{formatMessage({ id: 'crmInstType' })}</th>
                            <th>{formatMessage({ id: 'crmNextPay' })}</th>
                            <th>{formatMessage({ id: 'crmState' })}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((x) => (
                            <tr key={x.id} style={rowBg(formatMessage, x)}>
                                <td>
                                    <b>{x.data.studentName}</b>
                                    <LegTag x={x} />
                                    <Muted>
                                        {formatPhone(x.data.parentPhone)} · {formatMessage({ id: 'crmParentLower' })}
                                    </Muted>
                                </td>
                                <td>
                                    {dict.label('PAY_TYPE', x.data.payType)}
                                    <Muted>
                                        {x.legacy
                                            ? x.monthsLeft
                                                ? formatMessage({ id: 'crmMonthsLeft' }, { n: x.monthsLeft })
                                                : ''
                                            : formatMessage({ id: 'crmMonthsPaid' }, { n: months(x) })}
                                    </Muted>
                                </td>
                                <td>
                                    {x.accessUntil ? fmtDate(x.accessUntil) : <Muted>{formatMessage({ id: 'crmDateNotSet' })}</Muted>}
                                    <Muted>{money(nextSum(x))}</Muted>
                                </td>
                                <td>
                                    <State x={x} />
                                </td>
                                <td className="r">
                                    {x.accessUntil && x.payState !== 'PENDING' && x.payState !== 'DONE' && (
                                        <PrimaryButton size="small" onClick={() => setPaying(x)} sx={{ whiteSpace: 'nowrap' }}>
                                            {formatMessage({ id: 'crmParentPaid' })}
                                        </PrimaryButton>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!rows.length && <EmptyRow cols={5} text={formatMessage({ id: 'crmNoInstallments' })} />}
                    </tbody>
                </table>
            </TableWrap>
            <Hint>{formatMessage({ id: 'crmParentPaidHint' })}</Hint>
            {paying && <ReportDialog x={paying} onClose={() => setPaying(null)} />}
        </Card>
    )
}

const ReportDialog: React.FC<{ x: CrmIntakeView; onClose: () => void }> = ({ x, onClose }) => {
    const { formatMessage } = useIntl()
    const [amount, setAmount] = useState(String(nextSum(x) || ''))
    const [report, { isLoading }] = crmApi.useReportPaymentMutation()
    const n = months(x) + 1
    const submit = async () => {
        const sum = Number(amount)
        if (!sum) return
        try {
            await report({ intakeId: x.id, amount: sum }).unwrap()
            toast.success(formatMessage({ id: 'crmParentPaidToast' }, { name: x.data.studentName, n, sum: money(sum) }))
            onClose()
        } catch {
            /* общий обработчик */
        }
    }
    return (
        <CrmDialog title={formatMessage({ id: 'crmParentPaid' })} sub={`${x.data.studentName} · ${formatMessage({ id: 'crmMonthN' }, { n })}`} onClose={onClose}>
            <Box sx={{ margin: '14px 0' }}>
                <FieldLabel>{formatMessage({ id: 'crmPaidSum' })}</FieldLabel>
                <TextField size="small" fullWidth value={amount} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setAmount(digits(e.target.value))} />
            </Box>
            <Hint sx={{ marginBottom: '14px' }}>{formatMessage({ id: 'crmParentPaidModalHint' })}</Hint>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <OutlineButton onClick={onClose}>{formatMessage({ id: 'cancel' })}</OutlineButton>
                <PrimaryButton disabled={!Number(amount) || isLoading} onClick={submit}>
                    {formatMessage({ id: 'crmSave' })}
                </PrimaryButton>
            </Box>
        </CrmDialog>
    )
}

const ModView: React.FC<{ rows: CrmIntakeView[]; dict: CrmDict }> = ({ rows, dict }) => {
    const { formatMessage } = useIntl()
    const [confirming, setConfirming] = useState<CrmIntakeView | null>(null)
    const [reject, { isLoading: rejecting }] = crmApi.useRejectPaymentMutation()
    const noDate = rows.filter((x) => x.status === 'APPROVED' && !x.accessUntil && x.payState !== 'DONE')
    const list = rows.filter((x) => !!x.accessUntil && x.payState !== 'DONE')
    const pending = list.filter((x) => x.payState === 'PENDING').length

    const doReject = async (x: CrmIntakeView) => {
        if (!x.pendingPaymentId) return
        try {
            await reject(x.pendingPaymentId).unwrap()
            toast.info(formatMessage({ id: 'crmRejectedToast' }, { name: x.data.studentName }))
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <>
            {noDate.length > 0 && (
                <Card sx={{ border: `2px dashed ${ui.muted}` }}>
                    <CardHead title={formatMessage({ id: 'crmSetAccessTitle' }, { n: noDate.length })} />
                    <Hint sx={{ marginTop: '-6px', marginBottom: '8px' }}>{formatMessage({ id: 'crmSetAccessHint' })}</Hint>
                    <TableWrap minWidth={820}>
                        <table>
                            <thead>
                                <tr>
                                    <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                    <th>{formatMessage({ id: 'crmManager' })}</th>
                                    <th>{formatMessage({ id: 'crmInstType' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmPaidTotal' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmNextSum' })}</th>
                                    <th>{formatMessage({ id: 'crmAccessUntilShort' })}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {noDate.map((x) => (
                                    <SetAccessRow key={x.id} x={x} dict={dict} />
                                ))}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>
            )}
            <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <CardHead
                    title={
                        <>
                            {formatMessage({ id: 'crmWaitingPay' })}{' '}
                            {pending > 0 && <Pill tone="blue">{formatMessage({ id: 'crmPendingN' }, { n: pending })}</Pill>}
                        </>
                    }
                />
                <Legend />
                <TableWrap minWidth={900}>
                    <table>
                        <thead>
                            <tr>
                                <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                <th>{formatMessage({ id: 'crmManager' })}</th>
                                <th>{formatMessage({ id: 'crmInstType' })}</th>
                                <th>{formatMessage({ id: 'crmDueDate' })}</th>
                                <th className="num">{formatMessage({ id: 'crmExpectedSum' })}</th>
                                <th>{formatMessage({ id: 'crmState' })}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((x) => (
                                <tr key={x.id} style={rowBg(formatMessage, x)}>
                                    <td>
                                        <b>{x.data.studentName}</b>
                                        <LegTag x={x} />
                                    </td>
                                    <td>{x.sellerName || '-'}</td>
                                    <td>{dict.label('PAY_TYPE', x.data.payType)}</td>
                                    <td>{fmtDate(x.accessUntil)}</td>
                                    <td className="num">
                                        <b>{money(x.payState === 'PENDING' ? x.pendingPaymentSum : nextSum(x))}</b>
                                    </td>
                                    <td>
                                        <State x={x} />
                                    </td>
                                    <td className="r" style={{ whiteSpace: 'nowrap' }}>
                                        <Box sx={{ display: 'inline-flex', gap: '6px' }}>
                                            <PrimaryButton size="small" onClick={() => setConfirming(x)} sx={okBtn}>
                                                {formatMessage({ id: 'crmPayCame' })}
                                            </PrimaryButton>
                                            {x.pendingPaymentId && (
                                                <OutlineButton size="small" color="error" disabled={rejecting} onClick={() => doReject(x)}>
                                                    {formatMessage({ id: 'crmPayNotCame' })}
                                                </OutlineButton>
                                            )}
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            {!list.length && <EmptyRow cols={7} text={formatMessage({ id: 'crmNoWaitingPay' })} />}
                        </tbody>
                    </table>
                </TableWrap>
                <Hint>{formatMessage({ id: 'crmPayCameHint' })}</Hint>
            </Card>
            {confirming && <ConfirmDialog x={confirming} dict={dict} onClose={() => setConfirming(null)} />}
        </>
    )
}

const SetAccessRow: React.FC<{ x: CrmIntakeView; dict: CrmDict }> = ({ x, dict }) => {
    const { formatMessage } = useIntl()
    const [date, setDate] = useState(() => addMonthKey(todayKey()))
    const [sum, setSum] = useState(String(nextSum(x) || ''))
    const [setAccess, { isLoading }] = crmApi.useSetAccessUntilMutation()
    const submit = async () => {
        if (!date || daysTo(date) <= 0) {
            toast.error(formatMessage({ id: 'crmDateAfterToday' }))
            return
        }
        try {
            await setAccess({ intakeId: x.id, accessUntil: date, nextSum: Number(sum) || 0 }).unwrap()
            toast.success(formatMessage({ id: 'crmAccessSetToast' }, { name: x.data.studentName, date: fmtDate(date) }))
        } catch {
            /* общий обработчик */
        }
    }
    return (
        <tr>
            <td>
                <b>{x.data.studentName}</b>
            </td>
            <td>{x.sellerName || '-'}</td>
            <td>{dict.label('PAY_TYPE', x.data.payType)}</td>
            <td className="num">{money(x.paidTotal ?? x.data.paidSum)}</td>
            <td className="num">
                <TextField
                    size="small"
                    value={sum}
                    onChange={(e) => setSum(digits(e.target.value))}
                    inputProps={{ inputMode: 'numeric', 'aria-label': formatMessage({ id: 'crmNextSum' }), style: { textAlign: 'right' } }}
                    sx={{ width: 110 }}
                />
            </td>
            <td>
                <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} inputProps={{ 'aria-label': formatMessage({ id: 'crmAccessUntilShort' }) }} />
            </td>
            <td className="r">
                <PrimaryButton size="small" disabled={isLoading || !Number(sum)} onClick={submit}>
                    {formatMessage({ id: 'crmAssign' })}
                </PrimaryButton>
            </td>
        </tr>
    )
}

const ConfirmDialog: React.FC<{ x: CrmIntakeView; dict: CrmDict; onClose: () => void }> = ({ x, dict, onClose }) => {
    const { formatMessage } = useIntl()
    const base = x.accessUntil && daysTo(x.accessUntil) >= 0 ? x.accessUntil : todayKey()
    const pending = !!x.pendingPaymentId
    const [amount, setAmount] = useState(String((pending ? x.pendingPaymentSum : nextSum(x)) || ''))
    const [last, setLast] = useState(x.monthsLeft === 1)
    const [nextDate, setNextDate] = useState(() => addMonthKey(base))
    const [sum, setSum] = useState(String(nextSum(x) || ''))
    const [confirm, { isLoading }] = crmApi.useConfirmPaymentMutation()
    const [report, { isLoading: reporting }] = crmApi.useReportPaymentMutation()
    const ready = !!Number(amount) && (last || (daysTo(nextDate) > 0 && !!Number(sum)))

    const submit = async () => {
        if (!ready) return
        try {
            // продажник не отметил платёж - модератор сначала записывает его сам
            const paymentId = x.pendingPaymentId || (await report({ intakeId: x.id, amount: Number(amount) }).unwrap())?.pendingPaymentId
            if (!paymentId) return
            await confirm({ paymentId, amount: Number(amount), nextDate: last ? '' : nextDate, nextSum: last ? 0 : Number(sum), last }).unwrap()
            toast.success(
                last
                    ? formatMessage({ id: 'crmPayDoneToast' }, { name: x.data.studentName })
                    : formatMessage({ id: 'crmPayExtendedToast' }, { name: x.data.studentName, date: fmtDate(nextDate) }),
            )
            onClose()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <CrmDialog title={formatMessage({ id: 'crmPayCameTitle' })} sub={`${x.data.studentName} · ${dict.label('PAY_TYPE', x.data.payType)}`} onClose={onClose}>
            {pending && (
                <Hint sx={{ marginTop: '12px' }}>
                    {formatMessage({ id: 'crmSellerMarked' })} <b>{money(x.pendingPaymentSum)}</b> ({formatMessage({ id: 'crmMonthN' }, { n: months(x) + 1 })}).{' '}
                    {formatMessage({ id: 'crmCheckAccount' })}
                </Hint>
            )}
            <Box sx={{ marginTop: '12px' }}>
                <FieldLabel>{formatMessage({ id: 'crmReceivedSum' })}</FieldLabel>
                <TextField size="small" fullWidth value={amount} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setAmount(digits(e.target.value))} />
            </Box>
            <FormControlLabel
                sx={{ margin: '8px 0', '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                control={<Checkbox size="small" checked={last} onChange={(e) => setLast(e.target.checked)} />}
                label={
                    <>
                        {formatMessage({ id: 'crmLastPayment' })}
                        {!!x.monthsLeft && <Muted sx={{ display: 'inline' }}> · {formatMessage({ id: 'crmMonthsLeft' }, { n: x.monthsLeft })}</Muted>}
                    </>
                }
            />
            {!last && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '10px' }}>
                    <Box>
                        <FieldLabel>{formatMessage({ id: 'crmNextPayDate' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
                    </Box>
                    <Box>
                        <FieldLabel>{formatMessage({ id: 'crmNextSumLabel' })}</FieldLabel>
                        <TextField size="small" fullWidth value={sum} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setSum(digits(e.target.value))} />
                    </Box>
                </Box>
            )}
            <Hint sx={{ margin: '8px 0 14px' }}>{formatMessage({ id: last ? 'crmLastPaymentHint' : 'crmAccessExtendsHint' })}</Hint>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <OutlineButton onClick={onClose}>{formatMessage({ id: 'cancel' })}</OutlineButton>
                <PrimaryButton disabled={!ready || isLoading || reporting} onClick={submit} sx={okBtn}>
                    {formatMessage({ id: 'crmConfirm' })}
                </PrimaryButton>
            </Box>
        </CrmDialog>
    )
}

const RopView: React.FC<{ rows: CrmIntakeView[]; dict: CrmDict }> = ({ rows, dict }) => {
    const { formatMessage } = useIntl()
    const active = rows.filter((x) => x.payState !== 'DONE')
    const by = (s: string) => active.filter((x) => x.payState === s)
    const total = (list: CrmIntakeView[]) => money(list.reduce((t, x) => t + nextSum(x), 0))
    return (
        <>
            <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Kpis
                    items={[
                        { label: formatMessage({ id: 'crmKpiMonthly' }), value: active.length, sub: formatMessage({ id: 'crmExpected' }, { sum: total(active) }) },
                        { label: formatMessage({ id: 'crmKpiSoon' }), value: by('SOON').length, sub: total(by('SOON')) },
                        { label: formatMessage({ id: 'crmKpiLate' }), value: by('LATE').length, sub: total(by('LATE')), bad: by('LATE').length > 0 },
                        { label: formatMessage({ id: 'crmKpiPending' }), value: by('PENDING').length, sub: total(by('PENDING')) },
                    ]}
                />
                <Legend />
            </Card>
            <Card pad={false}>
                <TableWrap minWidth={860}>
                    <table>
                        <thead>
                            <tr>
                                <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                <th>{formatMessage({ id: 'crmManager' })}</th>
                                <th>{formatMessage({ id: 'crmInstType' })}</th>
                                <th className="num">{formatMessage({ id: 'crmPaidTotal' })}</th>
                                <th className="num">{formatMessage({ id: 'crmMonths' })}</th>
                                <th>{formatMessage({ id: 'crmNextPay' })}</th>
                                <th>{formatMessage({ id: 'crmState' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((x) => (
                                <tr key={x.id} style={rowBg(formatMessage, x)}>
                                    <td>
                                        <b>{x.data.studentName}</b>
                                        <LegTag x={x} />
                                    </td>
                                    <td>{x.sellerName || '-'}</td>
                                    <td>{dict.label('PAY_TYPE', x.data.payType)}</td>
                                    <td className="num">{money(x.paidTotal ?? x.data.paidSum)}</td>
                                    <td className="num">{x.legacy ? (x.monthsLeft ? formatMessage({ id: 'crmMonthsLeftShort' }, { n: x.monthsLeft }) : '-') : months(x)}</td>
                                    <td>
                                        {x.payState === 'DONE'
                                            ? '-'
                                            : `${x.accessUntil ? fmtDate(x.accessUntil) : formatMessage({ id: 'crmDateNotSet' })} · ${money(nextSum(x))}`}
                                    </td>
                                    <td>
                                        <State x={x} />
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && <EmptyRow cols={7} text={formatMessage({ id: 'crmNoMonthly' })} />}
                        </tbody>
                    </table>
                </TableWrap>
            </Card>
        </>
    )
}

export default InstallmentsPage
